/**
 * EmbedCard — rich preview card for opportunities / events / ventures inside a
 * community feed post. Mirrors the Discover / Events / Ventures listing card
 * style: cover on top, meta chips + big title + subtitle + org row + info chips
 * below. Whole card is a link to the source detail page.
 */
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Rocket, Radar, Building2, Clock, Users, IndianRupee } from 'lucide-react';

const fmtDateTime = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return null; }
};
const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return null; }
};

// Kind chip presets — coloured (mirrors listing card first chip).
const KIND = {
  opportunity: { label: 'Opportunity', Icon: Radar,    cls: 'bg-plum/10 text-plum border-plum/30' },
  event:       { label: 'Event',       Icon: Calendar, cls: 'bg-primary/10 text-primary border-primary/30' },
  venture:     { label: 'Venture',     Icon: Rocket,   cls: 'bg-success/10 text-success border-success/30' },
};

const Chip = ({ children, className = '', testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-grotesk font-medium ${className}`}>
    {children}
  </span>
);

export const EmbedCard = ({ embed, className = '' }) => {
  if (!embed) return null;
  const meta = KIND[embed.type] || KIND.opportunity;
  const KindIcon = meta.Icon;
  const cover = embed.cover_url;
  const deadline = embed.meta?.deadline ? fmtDate(embed.meta.deadline) : null;
  const startAt = embed.meta?.start_at ? fmtDateTime(embed.meta.start_at) : null;
  const location = embed.meta?.location || embed.meta?.city;
  const category = embed.meta?.category;
  const stage = embed.meta?.stage;
  const vertical = embed.meta?.vertical;
  const sector = embed.meta?.sector;

  return (
    <Link
      to={embed.url || '#'}
      target={embed.url?.startsWith('/v/') ? '_blank' : undefined}
      rel={embed.url?.startsWith('/v/') ? 'noopener noreferrer' : undefined}
      className={`group mt-4 block overflow-hidden rounded-2xl border border-border bg-card hover:shadow-lg hover:-translate-y-0.5 transition-all ${className}`}
      data-testid={`embed-${embed.type}-${embed.ref_id || 'x'}`}
    >
      {/* Cover */}
      {cover ? (
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-secondary">
          <img src={cover} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
        </div>
      ) : null}

      {/* Body */}
      <div className="p-4 sm:p-5">
        {/* Chips row: kind + category/stage */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <Chip className={meta.cls} testid={`embed-kind-${embed.type}`}>
            <KindIcon className="h-3 w-3" /> {meta.label.toUpperCase()}
          </Chip>
          {category && (
            <Chip className="bg-info/10 text-info border-info/30 capitalize">
              <Building2 className="h-3 w-3" /> {category}
            </Chip>
          )}
          {(vertical || sector) && (
            <Chip className="bg-secondary text-muted-foreground border-border capitalize">
              {vertical || sector}
            </Chip>
          )}
        </div>

        {/* Title */}
        <h3 className="font-display text-xl sm:text-2xl leading-tight text-foreground line-clamp-2" data-testid={`embed-title`}>
          {embed.title || 'Untitled'}
        </h3>

        {/* Subtitle */}
        {embed.subtitle && (
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1.5">{embed.subtitle}</p>
        )}

        {/* Org row — for events, `logo_url` is the host/speaker headshot →
            render as a round avatar. For opportunities / ventures it's an
            org logo → keep the rounded-square container. */}
        {embed.org && (
          <div className="flex items-center gap-2 mt-3">
            {embed.logo_url ? (
              <img
                src={embed.logo_url}
                alt=""
                className={`h-7 w-7 object-cover shrink-0 border border-border ${embed.type === 'event' ? 'rounded-full bg-secondary' : 'rounded-md bg-white'}`}
              />
            ) : (
              <div className="h-6 w-6 rounded-md bg-secondary border border-border grid place-items-center shrink-0">
                <Building2 className="h-3 w-3 text-muted-foreground" />
              </div>
            )}
            <span className="text-sm text-foreground/85 truncate">{embed.org}</span>
          </div>
        )}

        {/* Meta chips (deadline / start / location / stage) */}
        {(deadline || startAt || location || stage) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-xs">
            {startAt && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" /> {startAt}
              </span>
            )}
            {deadline && (
              <span className="inline-flex items-center gap-1 text-warning">
                <Clock className="h-3.5 w-3.5" /> Deadline · {deadline}
              </span>
            )}
            {location && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" /> {location}
              </span>
            )}
            {stage && (
              <span className="inline-flex items-center gap-1 text-primary capitalize">
                <Users className="h-3.5 w-3.5" /> {stage}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default EmbedCard;
